import React, { useState, useEffect } from 'react';
import { RefreshCw, User, Search, ShieldCheck, Download, Pencil, UserPlus, Ban, ShieldOff } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { apiGet } from '@/services/api';
import { Button, Card, LoadingState, EmptyState, Modal, Input, Select, Spinner } from '@/components/common';
import { useAuth } from '@/context/AuthContext';
import {
  getUserProfileAdmin,
  updateUserAdmin,
  setUserPasswordAdmin,
  createUser,
  type AdminUserProfile,
} from '@/services/adminUsers.service';
import type { HalCollection } from '@/types';

// Extrage un mesaj lizibil dintr-o eroare (ex. body-ul 409/400 de la backend).
function extractErrorMessage(err: unknown, fallback: string): string {
  const anyErr = err as { response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message || fallback;
}

interface UserData {
  id: number;
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  admin: boolean;
  status: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Modal de editare a unui cont (admin-only). Oglindește câmpurile pe care un
// utilizator și le editează singur în Setări: Prenume, Nume, Funcție,
// Departament, Telefon + (opțional) o parolă nouă.
// ---------------------------------------------------------------------------
interface EditUserModalProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditUserModal({ userId, isOpen, onClose, onSaved }: EditUserModalProps) {
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.id === userId;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [profile, setProfile] = useState<AdminUserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [functie, setFunctie] = useState('');
  const [departament, setDepartament] = useState('');
  const [telefon, setTelefon] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [status, setStatus] = useState(1);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Încarcă profilul la deschidere (sau când se schimbă utilizatorul țintă).
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setPasswordError(null);
    setNewPassword('');
    setConfirmPassword('');

    getUserProfileAdmin(userId)
      .then((data) => {
        if (cancelled) return;
        setProfile(data);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setFunctie(data.customFields?.functie || '');
        setDepartament(data.customFields?.departament || '');
        setTelefon(data.customFields?.telefon || '');
        setIsAdmin(!!data.admin);
        setStatus(data.status === 3 ? 3 : 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load user profile:', err);
        setLoadError('Eroare la încărcarea datelor utilizatorului');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  const handleSave = async () => {
    if (saving) return;
    setSaveError(null);
    setPasswordError(null);

    // Validare parolă (doar dacă a fost completată)
    const wantsPasswordChange = newPassword.length > 0 || confirmPassword.length > 0;
    if (wantsPasswordChange) {
      if (newPassword.length < 8) {
        setPasswordError('Parola trebuie să aibă cel puțin 8 caractere');
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('Cele două parole nu coincid');
        return;
      }
    }

    setSaving(true);
    try {
      const update: Parameters<typeof updateUserAdmin>[1] = {
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        functie: functie.trim(),
        departament: departament.trim(),
        telefon: telefon.trim(),
      };
      // Nu-ți poți retrage propriul rol de admin / nu-ți poți bloca propriul cont.
      if (!isSelf) {
        update.admin = isAdmin;
        update.status = status;
      }
      await updateUserAdmin(userId, update);

      if (wantsPasswordChange) {
        await setUserPasswordAdmin(userId, newPassword);
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save user:', err);
      setSaveError(extractErrorMessage(err, 'Eroare la salvarea modificărilor. Încearcă din nou.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Editează utilizator"
      size="lg"
      closeOnOverlayClick={!saving}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Anulează
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={saving}
            disabled={saving || loading || !!loadError}
          >
            {saving ? 'Se salvează...' : 'Salvează'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : loadError ? (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {loadError}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Context: login & email (read-only) */}
          {profile && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Utilizator"
                value={`@${profile.login}`}
                readOnly
                disabled
              />
              <Input
                label="Email"
                value={profile.email || '-'}
                readOnly
                disabled
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Prenume"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={saving}
            />
            <Input
              label="Nume"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={saving}
            />
          </div>

          <Input
            label="Funcție"
            value={functie}
            onChange={(e) => setFunctie(e.target.value)}
            disabled={saving}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Departament"
              value={departament}
              onChange={(e) => setDepartament(e.target.value)}
              disabled={saving}
            />
            <Input
              label="Telefon"
              value={telefon}
              onChange={(e) => setTelefon(e.target.value)}
              disabled={saving}
            />
          </div>

          {/* Tip cont & Status cont */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Tip cont"
              value={isAdmin ? 'admin' : 'user'}
              onChange={(e) => setIsAdmin(e.target.value === 'admin')}
              disabled={saving || isSelf}
              hint={isSelf ? 'Nu îți poți modifica propriul rol.' : undefined}
              options={[
                { value: 'user', label: 'Utilizator' },
                { value: 'admin', label: 'Administrator' },
              ]}
            />
            <Select
              label="Status cont"
              value={String(status)}
              onChange={(e) => setStatus(Number(e.target.value))}
              disabled={saving || isSelf}
              hint={isSelf ? 'Nu îți poți bloca propriul cont.' : undefined}
              options={[
                { value: '1', label: 'Activ' },
                { value: '3', label: 'Blocat' },
              ]}
            />
          </div>

          {/* Parola nouă (opțional) */}
          <div className="pt-4 mt-2 border-t border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-1">Parolă nouă</p>
            <p className="text-xs text-gray-500 mb-3">
              Lasă necompletat pentru a păstra parola actuală. Minimum 8 caractere.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Parolă nouă (opțional)"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={saving}
                error={passwordError || undefined}
              />
              <Input
                label="Confirmă parola"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          {saveError && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
              {saveError}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal de creare a unui cont nou (admin-only). Impune login/email unic
// (verificat de backend), parolă min. 8 caractere cu confirmare.
// ---------------------------------------------------------------------------
interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateUserModal({ isOpen, onClose, onCreated }: CreateUserModalProps) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [login, setLogin] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [functie, setFunctie] = useState('');
  const [departament, setDepartament] = useState('');
  const [telefon, setTelefon] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Reset la fiecare deschidere.
  useEffect(() => {
    if (!isOpen) return;
    setSaving(false);
    setSaveError(null);
    setPasswordError(null);
    setLogin('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setIsAdmin(false);
    setFunctie('');
    setDepartament('');
    setTelefon('');
    setPassword('');
    setConfirmPassword('');
  }, [isOpen]);

  const handleCreate = async () => {
    if (saving) return;
    setSaveError(null);
    setPasswordError(null);

    if (!login.trim()) {
      setSaveError('Numele de utilizator este obligatoriu.');
      return;
    }
    if (!firstName.trim()) {
      setSaveError('Prenumele este obligatoriu.');
      return;
    }
    if (!lastName.trim()) {
      setSaveError('Numele este obligatoriu.');
      return;
    }
    if (!email.trim()) {
      setSaveError('Email-ul este obligatoriu.');
      return;
    }
    if (password.length < 8) {
      setPasswordError('Parola trebuie să aibă cel puțin 8 caractere');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Cele două parole nu coincid');
      return;
    }

    setSaving(true);
    try {
      const res = await createUser({
        login: login.trim(),
        firstname: firstName.trim(),
        lastname: lastName.trim(),
        mail: email.trim(),
        admin: isAdmin,
        password,
        functie: functie.trim() || undefined,
        departament: departament.trim() || undefined,
        telefon: telefon.trim() || undefined,
      });
      if (!res.success) {
        setSaveError(res.message || 'Eroare la crearea contului.');
        return;
      }
      onCreated();
      onClose();
    } catch (err) {
      console.error('Failed to create user:', err);
      setSaveError(extractErrorMessage(err, 'Eroare la crearea contului. Încearcă din nou.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Adaugă cont nou"
      size="lg"
      closeOnOverlayClick={!saving}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Anulează
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            isLoading={saving}
            disabled={saving}
          >
            {saving ? 'Se creează...' : 'Creează cont'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Utilizator (login)"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            disabled={saving}
            autoComplete="off"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={saving}
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Prenume"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={saving}
          />
          <Input
            label="Nume"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={saving}
          />
        </div>

        <Select
          label="Tip cont"
          value={isAdmin ? 'admin' : 'user'}
          onChange={(e) => setIsAdmin(e.target.value === 'admin')}
          disabled={saving}
          options={[
            { value: 'user', label: 'Utilizator' },
            { value: 'admin', label: 'Administrator' },
          ]}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Funcție (opțional)"
            value={functie}
            onChange={(e) => setFunctie(e.target.value)}
            disabled={saving}
          />
          <Input
            label="Departament (opțional)"
            value={departament}
            onChange={(e) => setDepartament(e.target.value)}
            disabled={saving}
          />
        </div>

        <Input
          label="Telefon (opțional)"
          value={telefon}
          onChange={(e) => setTelefon(e.target.value)}
          disabled={saving}
        />

        <div className="pt-4 mt-2 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-1">Parolă</p>
          <p className="text-xs text-gray-500 mb-3">Minimum 8 caractere.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Parolă"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={saving}
              error={passwordError || undefined}
            />
            <Input
              label="Confirmă parola"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {saveError && (
          <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
            {saveError}
          </div>
        )}
      </div>
    </Modal>
  );
}

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiGet<HalCollection<UserData>>('/auth/data/admin/users');
      setUsers(response._embedded?.elements || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Eroare la încărcarea utilizatorilor');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Blocare/deblocare rapidă a unui cont (status 1 <-> 3).
  const handleToggleBlock = async (user: UserData) => {
    if (togglingId !== null) return;
    const currentlyBlocked = user.status === 3;
    setTogglingId(user.id);
    setError(null);
    try {
      await updateUserAdmin(user.id, { status: currentlyBlocked ? 1 : 3 });
      await loadUsers();
    } catch (err) {
      console.error('Failed to toggle block:', err);
      setError(extractErrorMessage(err, 'Eroare la schimbarea statusului contului.'));
    } finally {
      setTogglingId(null);
    }
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.login.toLowerCase().includes(query) ||
      user.firstName?.toLowerCase().includes(query) ||
      user.lastName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  });

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: return { label: 'Activ', color: 'bg-green-100 text-green-700' };
      case 2: return { label: 'Înregistrat', color: 'bg-yellow-100 text-yellow-700' };
      case 3: return { label: 'Blocat', color: 'bg-red-100 text-red-700' };
      default: return { label: 'Necunoscut', color: 'bg-gray-100 text-gray-700' };
    }
  };

  // Export în Excel (.xlsx). SheetJS e încărcat lazy (doar la click) ca să nu
  // îngroașe bundle-ul principal. Exportă exact ce se vede (lista filtrată).
  const handleExport = async () => {
    if (exporting || filteredUsers.length === 0) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const rows = filteredUsers.map((u) => ({
        'Prenume': u.firstName || '',
        'Nume': u.lastName || '',
        'Utilizator': u.login,
        'Email': u.email || '',
        'Rol': u.admin ? 'Administrator' : 'Utilizator',
        'Status': getStatusLabel(u.status).label,
        'Data creării': u.createdAt ? format(parseISO(u.createdAt), 'dd.MM.yyyy', { locale: ro }) : '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 24 }, { wch: 30 }, { wch: 14 }, { wch: 12 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Utilizatori');
      const d = new Date();
      const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `utilizatori-${stamp}.xlsx`);
    } catch (err) {
      console.error('Export Excel eșuat:', err);
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return <LoadingState message="Se încarcă utilizatorii..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-blue-500 text-white">
            <User className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Utilizatori</h1>
            <p className="text-gray-600 mt-1">Lista utilizatorilor din sistem</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredUsers.length} utilizatori
          </span>
          <Button
            variant="secondary"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={handleExport}
            isLoading={exporting}
            disabled={exporting || filteredUsers.length === 0}
          >
            Export Excel
          </Button>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadUsers}
          >
            Reîncarcă
          </Button>
          <Button
            variant="primary"
            leftIcon={<UserPlus className="w-4 h-4" />}
            onClick={() => setCreatingUser(true)}
          >
            Adaugă cont nou
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută utilizatori..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="users"
              title="Nu există utilizatori"
              description={searchQuery ? "Nu s-au găsit utilizatori care să corespundă căutării." : "Nu există utilizatori în sistem."}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Utilizator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Creat
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const status = getStatusLabel(user.status);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-700 font-semibold">
                              {(user.firstName?.[0] || user.login[0]).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-gray-500">@{user.login}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.email || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.admin ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            <ShieldCheck className="w-3 h-3" />
                            Administrator
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            <User className="w-3 h-3" />
                            Utilizator
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.createdAt
                          ? format(parseISO(user.createdAt), 'dd MMM yyyy', { locale: ro })
                          : '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {currentUser?.id !== user.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={
                                user.status === 3 ? (
                                  <ShieldOff className="w-4 h-4" />
                                ) : (
                                  <Ban className="w-4 h-4" />
                                )
                              }
                              onClick={() => handleToggleBlock(user)}
                              isLoading={togglingId === user.id}
                              disabled={togglingId !== null}
                              aria-label={
                                user.status === 3
                                  ? `Deblochează ${user.login}`
                                  : `Blochează ${user.login}`
                              }
                            >
                              {user.status === 3 ? 'Deblochează' : 'Blochează'}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={<Pencil className="w-4 h-4" />}
                            onClick={() => setEditingUserId(user.id)}
                            aria-label={`Editează ${user.login}`}
                          >
                            Editează
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editingUserId !== null && (
        <EditUserModal
          userId={editingUserId}
          isOpen={editingUserId !== null}
          onClose={() => setEditingUserId(null)}
          onSaved={loadUsers}
        />
      )}

      <CreateUserModal
        isOpen={creatingUser}
        onClose={() => setCreatingUser(false)}
        onCreated={loadUsers}
      />
    </div>
  );
}
